import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import moment from "moment";
import Tabs from "../Tabs";
import { DateRange } from "@material-symbols-svg/react";

import CGMCalendarBottomSheet from "../bottom-sheet/cgm/CGMCalendarBottomSheet";
import CGMSensorCycleBottomSheet from "../bottom-sheet/cgm/CGMSensorCycleBottomSheet";
import { useAppSelector, useAppDispatch } from "src/redux/hooks";
import {
  getCGMCalendarRequest,
  getCGMCumulativeLogsRequest,
  getDailyLogsRequest,
} from "src/redux/slices/cgmSlice";
import type { CgmDateRange } from "src/types/cgm.types";

interface CgmPeriodNavigatorProps {
  className?: string;
  tabs: { id: string; title: string }[];
  activeTab: string;
  periodOffset: number;
  onTabChange: (tab: string) => void;
  onPrev: () => void;
  onNext: () => void;
  hideTabs?: boolean;
  onDateSelect?: (date: string) => void;
  onDateRangeSelect?: (dateRange: CgmDateRange) => void;
}

const CgmPeriodNavigator: React.FC<CgmPeriodNavigatorProps> = ({
  className,
  tabs,
  activeTab,
  periodOffset,
  onTabChange,
  onPrev,
  onNext,
  hideTabs = false,
  onDateSelect,
  onDateRangeSelect,
}) => {
  const dispatch = useAppDispatch();
  const cgm = useAppSelector((state) => state.cgm);
  const { cgmCalendar, cgmCumulativeLogs, dailyLogs } = cgm;

  const [showCalendar, setShowCalendar] = useState(false);
  const [showDateRangeSheet, setShowDateRangeSheet] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedDateRange, setSelectedDateRange] =
    useState<CgmDateRange | null>(null);

  const selectedMoment = moment(selectedDate);

  // Fetch calendar for current month on mount and when tab changes
  useEffect(() => {
    const now = moment();
    dispatch(
      getCGMCalendarRequest({
        month: now.month() + 1,
        year: now.year(),
      })
    );
  }, [dispatch, activeTab]);

  // Available days (from monthDates) and sensor cycles
  const availableDates = useMemo(() => {
    const dates = cgmCalendar.data?.monthDates ?? [];
    return dates
      .map(({ date }) =>
        moment(date?.split("T")[0] ?? date).format("YYYY-MM-DD")
      )
      .filter(Boolean)
      .sort();
  }, [cgmCalendar.data?.monthDates]);

  console.log("[CgmPeriodNavigator] cgmCalendar:", cgmCalendar);

  const sensorCycles = useMemo(
    () => cgmCalendar.data?.sensorCycles ?? [],
    [cgmCalendar.data?.sensorCycles]
  );

  // Daily: index into availableDates; default to today or last available
  const dailyIndex = useMemo(() => {
    const d = moment(selectedDate).format("YYYY-MM-DD");
    const idx = availableDates.indexOf(d);
    if (idx >= 0) return idx;
    if (availableDates.length > 0) return availableDates.length - 1;
    return -1;
  }, [selectedDate, availableDates]);

  const canPrevDaily = dailyIndex > 0;
  const canNextDaily =
    dailyIndex < availableDates.length - 1 && dailyIndex >= 0;

  // Cumulative: index into sensorCycles
  const cumulativeIndex = useMemo(() => {
    if (!selectedDateRange && sensorCycles.length > 0)
      return sensorCycles.length - 1;
    if (!selectedDateRange) return -1;
    const idx = sensorCycles.findIndex(
      (c) =>
        c.start_date === selectedDateRange.start_date &&
        c.end_date === selectedDateRange.end_date
    );
    return idx >= 0 ? idx : sensorCycles.length - 1;
  }, [selectedDateRange, sensorCycles]);

  const canPrevCumulative = cumulativeIndex > 0;
  const canNextCumulative =
    cumulativeIndex < sensorCycles.length - 1 && cumulativeIndex >= 0;

  // Set default selected date range when sensor cycles load
  useEffect(() => {
    if (
      activeTab === "cumulative" &&
      sensorCycles.length > 0 &&
      !selectedDateRange
    ) {
      setSelectedDateRange(sensorCycles[sensorCycles.length - 1]);
    }
  }, [activeTab, sensorCycles, selectedDateRange]);

  // Fetch cumulative report when cumulative tab is active and a range is selected (incl. tab switch & default range).
  useEffect(() => {
    if (activeTab !== "cumulative" || !selectedDateRange) return;
    dispatch(
      getCGMCumulativeLogsRequest({
        startDate: selectedDateRange.start_date,
        endDate: selectedDateRange.end_date,
      })
    );
  }, [activeTab, selectedDateRange, dispatch]);

  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      setShowCalendar(false);
      const dateStr = moment(date).format("YYYY-MM-DD");
      dispatch(getDailyLogsRequest({ startDate: dateStr }));
      console.log("[CgmPeriodNavigator] Daily date selected:", dateStr);
      onDateSelect?.(dateStr);
    },
    [dispatch, onDateSelect]
  );

  const handleDateRangeSelect = useCallback(
    (dateRange: CgmDateRange) => {
      setSelectedDateRange(dateRange);
      setShowDateRangeSheet(false);
      console.log(
        "[CgmPeriodNavigator] Cumulative date range selected:",
        dateRange
      );
      onDateRangeSelect?.(dateRange);
    },
    [onDateRangeSelect]
  );

  const handlePrev = useCallback(() => {
    if (activeTab === "daily" && canPrevDaily) {
      const prevDate = availableDates[dailyIndex - 1];
      setSelectedDate(moment(prevDate).toDate());
      dispatch(getDailyLogsRequest({ startDate: prevDate }));
      console.log("[CgmPeriodNavigator] Daily prev:", prevDate);
      onDateSelect?.(prevDate);
    } else if (activeTab === "cumulative" && canPrevCumulative) {
      const prevRange = sensorCycles[cumulativeIndex - 1];
      setSelectedDateRange(prevRange);
      console.log("[CgmPeriodNavigator] Cumulative prev:", prevRange);
      onDateRangeSelect?.(prevRange);
    } else {
      onPrev();
    }
  }, [
    activeTab,
    canPrevDaily,
    canPrevCumulative,
    dailyIndex,
    cumulativeIndex,
    availableDates,
    sensorCycles,
    onDateSelect,
    onDateRangeSelect,
    onPrev,
  ]);

  const handleNext = useCallback(() => {
    if (activeTab === "daily" && canNextDaily) {
      const nextDate = availableDates[dailyIndex + 1];
      setSelectedDate(moment(nextDate).toDate());
      dispatch(getDailyLogsRequest({ startDate: nextDate }));
      console.log("[CgmPeriodNavigator] Daily next:", nextDate);
      onDateSelect?.(nextDate);
    } else if (activeTab === "cumulative" && canNextCumulative) {
      const nextRange = sensorCycles[cumulativeIndex + 1];
      setSelectedDateRange(nextRange);
      console.log("[CgmPeriodNavigator] Cumulative next:", nextRange);
      onDateRangeSelect?.(nextRange);
    } else {
      onNext();
    }
  }, [
    activeTab,
    canNextDaily,
    canNextCumulative,
    dailyIndex,
    cumulativeIndex,
    availableDates,
    sensorCycles,
    onDateSelect,
    onDateRangeSelect,
    onNext,
  ]);

  const periodLabel = useMemo(() => {
    if (activeTab === "daily") {
      const d = moment(selectedDate);
      const today = moment().format("YYYY-MM-DD");
      const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
      const dateStr = d.format("YYYY-MM-DD");
      if (dateStr === today) return "Today";
      if (dateStr === yesterday) return "Yesterday";
      return d.format("D MMM");
    }
    if (activeTab === "cumulative") {
      if (!selectedDateRange) return "Select date range";
      return `${moment(selectedDateRange.start_date).format(
        "MMM D"
      )} – ${moment(selectedDateRange.end_date).format("MMM D")}`;
    }
    return "";
  }, [activeTab, selectedDate, selectedDateRange]);

  const isPrevDisabled =
    activeTab === "daily"
      ? !canPrevDaily
      : activeTab === "cumulative"
      ? !canPrevCumulative
      : periodOffset <= 0;

  const isNextDisabled =
    activeTab === "daily"
      ? !canNextDaily
      : activeTab === "cumulative"
      ? !canNextCumulative
      : false;

  const handleDateButtonClick = () => {
    if (activeTab === "daily") {
      setShowCalendar(true);
    } else if (activeTab === "cumulative") {
      setShowDateRangeSheet(true);
    }
  };

  // Console.log data when daily logs or cumulative logs are fetched
  useEffect(() => {
    if (activeTab === "daily" && dailyLogs.data) {
      console.log("[CgmPeriodNavigator] Daily logs data:", dailyLogs.data);
    }
  }, [activeTab, dailyLogs.data]);

  useEffect(() => {
    if (activeTab === "cumulative" && cgmCumulativeLogs.data) {
      console.log(
        "[CgmPeriodNavigator] Cumulative report data:",
        cgmCumulativeLogs.data
      );
    }
  }, [activeTab, cgmCumulativeLogs.data]);

  return (
    <div className={`flex items-center justify-between gap-2 ${className}`}>
      <div className="w-5/12 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={handlePrev}
          disabled={isPrevDisabled}
          className="bg-white rounded-full p-1.5 border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Previous period"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-gray-600 font-medium flex-1 text-center">
          {periodLabel}
        </span>
        <button
          type="button"
          onClick={handleNext}
          disabled={isNextDisabled}
          className="bg-white rounded-full p-1.5 border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Next period"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="w-7/12 flex items-center gap-2">
        <button
          onClick={handleDateButtonClick}
          className="bg-white rounded-lg p-2.5 border border-gray-200"
        >
          <DateRange className="w-4 h-4" />
        </button>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={(tab) => onTabChange(tab.id as string)}
        />
      </div>

      {activeTab === "daily" && showCalendar && (
        <CGMCalendarBottomSheet
          open={showCalendar}
          close={() => setShowCalendar(false)}
          data={{
            selectedDate,
            selectedMonth: selectedMoment.month() + 1,
            selectedYear: selectedMoment.year(),
          }}
          onDateSelect={handleDateSelect}
        />
      )}

      {activeTab === "cumulative" && showDateRangeSheet && (
        <CGMSensorCycleBottomSheet
          open={showDateRangeSheet}
          close={() => setShowDateRangeSheet(false)}
          dateRanges={sensorCycles}
          onSelectDateRange={handleDateRangeSelect}
        />
      )}
    </div>
  );
};

export default CgmPeriodNavigator;
